<template>
  <base-drawer class="affiliate-detail-drawer" :show="show" :title="affiliate ? affiliate.name : ''" @close="$emit('close')">
    <template v-if="affiliate">
      <drawer-section title="Overview" icon="icon-badge" collapsible :expanded.sync="sections.overview">
        <div class="detail-row text-white"><span>Type</span><span>{{ typeLabel(affiliate.type) }}</span></div>
        <div class="detail-row text-white">
          <span>Tier</span>
          <span
            v-if="canUpdate"
            class="status-badge-inline status-badge-inline--editable status-badge-inline--unassigned"
            style="background: rgba(255, 255, 255, 0.1); color: rgba(255, 255, 255, 0.85);"
          >
            {{ currentTierName }}
            <i v-if="savingTier" class="el-icon-loading status-badge-inline__icon"></i>
            <i v-else class="el-icon-arrow-down status-badge-inline__icon"></i>
            <el-select
              :value="selectedTierId"
              class="status-badge-inline__select"
              placeholder=""
              :disabled="savingTier"
              @change="onTierChange"
            >
              <el-option
                v-for="t in tierOptions"
                :key="t.id"
                :value="t.id"
                :label="t.name"
              ></el-option>
            </el-select>
          </span>
          <span v-else>{{ currentTierName }}</span>
        </div>
        <div class="detail-row text-white"><span>Email</span><span>{{ affiliate.email }}</span></div>
        <div class="detail-row text-white">
          <span>Source</span>
          <span><badge type="default">{{ affiliate.ref_code }}</badge></span>
        </div>
        <div class="detail-row text-white">
          <span>Medium</span>
          <span><badge type="default">{{ affiliate.utm_medium || 'affiliate' }}</badge></span>
        </div>
        <div class="detail-row text-white">
          <span>Campaign</span>
          <span><badge type="default">{{ affiliate.utm_campaign || 'affiliate' }}</badge></span>
        </div>
        <div class="detail-row text-white" v-if="(affiliate.promo_codes || []).length > 0">
          <span>Promo codes</span>
          <span>
            <badge v-for="p in affiliate.promo_codes" :key="p.id" type="primary" class="ml-1">{{ p.name }}</badge>
          </span>
        </div>
        <div class="mt-2">
          <a class="section-header__link text-primary" href="#" @click.prevent="copyLink">
            <i class="tim-icons icon-single-copy-04" style="font-size: 10px;"></i> Copy booking link
          </a>
        </div>
      </drawer-section>

      <drawer-section title="Performance" icon="icon-chart-bar-32" collapsible :expanded.sync="sections.performance">
        <loading-panel v-if="loadingPerformance" />
        <template v-else-if="performance">
          <div class="detail-row text-white"><span>Tracked visits</span><span>{{ performance.visits }}</span></div>
          <div class="detail-row text-white"><span>Attributed sales</span>
            <span>{{ performance.sales_count }} ({{ formatMoney(performance.sales_sum) }})</span>
          </div>
          <div class="detail-row text-white"><span>Earned</span><span>{{ formatMoney(performance.earned) }}</span></div>
          <div class="detail-row text-white"><span>Reversed</span><span>{{ formatMoney(performance.reversed) }}</span></div>
          <div class="detail-row text-white"><span>Paid out</span><span>{{ formatMoney(performance.paid) }}</span></div>
          <div class="detail-row text-white"><span>Owed</span>
            <span class="font-weight-bold">{{ formatMoney(performance.owed) }}</span>
          </div>
        </template>
      </drawer-section>

      <drawer-section title="Ledger" icon="icon-notes" collapsible :expanded.sync="sections.ledger">
        <div v-if="performance" class="mb-3">
          <div class="detail-row text-white"><span>Earned</span><span>{{ formatMoney(performance.earned) }}</span></div>
          <div class="detail-row text-white"><span>Reversed</span><span>{{ formatMoney(performance.reversed) }}</span></div>
          <div class="detail-row text-white"><span>Paid out</span><span>{{ formatMoney(performance.paid) }}</span></div>
          <div class="detail-row text-white"><span>Owed</span>
            <span class="font-weight-bold">{{ formatMoney(performance.owed) }}</span>
          </div>
        </div>
        <div v-if="canUpdate && !attachOpen && !retierMode" class="mb-2">
          <a class="section-header__link text-primary" href="#" @click.prevent="attachOpen = true">
            <i class="tim-icons icon-simple-add" style="font-size: 10px;"></i> Add transaction
          </a>
          <a v-if="eligibleEntries.length > 0" class="section-header__link text-primary ml-3" href="#" @click.prevent="openRetier">
            <i class="tim-icons icon-pencil" style="font-size: 10px;"></i> Change tier
          </a>
        </div>
        <div v-if="canUpdate && attachOpen" class="mb-3">
          <base-input
            label="Add a transaction"
            v-model="attachForm.order_number"
            placeholder="Transaction number"
            :error="attachErr"
            @keydown.enter.prevent="attachTransaction"
          />
          <base-button
            variant="outlined"
            tone="primary"
            size="sm"
            :disabled="attaching || !attachForm.order_number"
            :loading="attaching"
            @click="attachTransaction"
          ><i class="tim-icons icon-simple-add"></i>Add transaction</base-button>
          <a class="section-header__link text-muted ml-3" href="#" @click.prevent="closeAttach">Cancel</a>
          <info-hint class="mt-2" variant="neutral" icon="icon-alert-circle-exc">
            Use this for a paid sale that did not come through the affiliate's link or promo code. The commission is calculated at the affiliate's tier.
          </info-hint>
        </div>
        <div v-if="canUpdate && retierMode" class="retier-toolbar mb-2">
          <div class="d-flex align-items-center">
            <el-select class="select-primary retier-toolbar__select" size="small" v-model="retierTierId" placeholder="Choose a tier">
              <el-option class="select-primary" v-for="t in activeTierOptions" :key="t.id" :value="t.id" :label="t.name" />
            </el-select>
            <base-button
              variant="outlined"
              tone="primary"
              size="sm"
              class="ml-2"
              :disabled="selectedIds.size === 0 || !retierTierId || previewing"
              :loading="previewing"
              @click="previewRetier"
            ><i class="tim-icons icon-pencil"></i>Change tier<template v-if="selectedIds.size > 0"> ({{ selectedIds.size }})</template></base-button>
            <a class="section-header__link text-muted ml-3" href="#" @click.prevent="closeRetier">Cancel</a>
            <a
              v-if="selectedIds.size < eligibleEntries.length"
              class="section-header__link text-primary ml-3"
              href="#"
              @click.prevent="selectAll"
            >Select all</a>
          </div>
        </div>
        <p v-if="!loadingPerformance && ledger.length === 0" class="mb-0">
          <small>No commission activity yet. Commission appears here after an attributed booking is fully paid.</small>
        </p>
        <div v-for="entry in ledger" :key="entry.id" class="ledger-row">
          <div class="d-flex justify-content-between align-items-center">
            <span class="d-inline-flex align-items-center">
              <base-checkbox
                v-if="retierMode && canRemove(entry)"
                class="ledger-select"
                :checked="isSelected(entry)"
                @input="toggleSelected(entry, $event)"
              />
              <badge :type="entryBadgeType(entry.entry_type)">{{ entry.entry_type }}</badge>
              <a
                v-if="entry.order_number"
                class="ml-2 text-primary"
                :href="`/purchases/transactions/details?order_number=${entry.order_number}`"
                target="_blank"
                rel="noopener"
              ><small>#{{ entry.order_number }} <i class="tim-icons el-icon-top-right" style="font-size: 10px;"></i></small></a>
              <el-tooltip v-if="canRemove(entry)" content="Remove from ledger" placement="top">
                <a
                  class="text-muted ml-2"
                  href="#"
                  aria-label="Remove from ledger"
                  @click.prevent="removePrompt = entry"
                ><i class="tim-icons icon-trash-simple" style="font-size: 12px;"></i></a>
              </el-tooltip>
            </span>
            <span class="text-white">
              <del v-if="entry.entry_type === 'earned' && isReversed(entry)">{{ formatMoney(entry.commission_amount) }}</del>
              <template v-else>{{ formatMoney(entry.commission_amount) }}</template>
            </span>
          </div>
          <small>
            {{ formatDate(entry.timestamp) }}
            <template v-if="entry.entry_type === 'paid'">
              via {{ entry.payout_method }}<template v-if="entry.payout_reference"> ({{ entry.payout_reference }})</template>
              <template v-if="entry.recorded_by_first_name"> by {{ entry.recorded_by_first_name }} {{ entry.recorded_by_last_name }}</template>
            </template>
            <template v-else-if="entry.sale_amount != null">
              sale {{ formatMoney(entry.sale_amount) }}<template v-if="entry.tier_name"> ({{ entry.tier_name }})</template>
              <template v-if="entry.entry_type === 'earned' && entry.recorded_by_first_name"> added by {{ entry.recorded_by_first_name }} {{ entry.recorded_by_last_name }}</template>
              <template v-if="entry.entry_type === 'reversed' && entry.recorded_by_first_name"> removed by {{ entry.recorded_by_first_name }} {{ entry.recorded_by_last_name }}</template>
            </template>
          </small>
        </div>
      </drawer-section>

      <drawer-section v-if="canUpdate" title="Record payout" icon="icon-money-coins" collapsible :expanded.sync="sections.payout">
        <info-hint>
          Payouts happen outside OTC (Zelle, PayPal, Venmo). Record one here to reduce the amount owed.
        </info-hint>
        <label>Amount</label>
        <money-input v-model="payoutForm.amount" :error="payoutErr" class="mb-3" />
        <base-input label="Method">
          <el-select class="select-primary" v-model="payoutForm.payout_method">
            <el-option class="select-primary" v-for="m in methodOptions" :key="m.value" :value="m.value" :label="m.label" />
          </el-select>
        </base-input>
        <base-input label="Reference (optional)" v-model="payoutForm.payout_reference" placeholder="Confirmation number" />
        <base-input label="Note (optional)" v-model="payoutForm.payout_note" placeholder="e.g. June commission" />
        <div class="mt-2">
          <base-button
            type="primary"
            size="sm"
            :disabled="owedCents <= 0 || recordingPayout"
            :loading="recordingPayout"
            @click="recordPayout"
          >Record payout</base-button>
        </div>
      </drawer-section>

      <base-alert-dialog
        :show="!!removePrompt"
        :title="removePrompt ? `Remove #${removePrompt.order_number} from the ledger?` : ''"
        variant="danger"
        show-cancel
        confirm-text="Remove"
        cancel-text="Keep it"
        confirm-variant="danger"
        :loading="removing"
        :dismissible="!removing"
        @confirm="removeTransaction"
        @cancel="removePrompt = null"
      >
        <template v-if="removePrompt">
          This removes {{ formatMoney(removePrompt.commission_amount) }} of commission from {{ affiliate.name }}'s ledger.
          Owed after removal: {{ centsToDisplay(owedAfterRemovalCents) }}.
          The sale stays on record and cannot be added back.
        </template>
      </base-alert-dialog>
      <base-alert-dialog
        :show="!!retierPrompt"
        :title="retierPrompt ? `Move ${pluralEntries(retierPrompt.rows.length)} to ${retierPrompt.tier.name}?` : ''"
        variant="warning"
        show-cancel
        confirm-text="Change tier"
        cancel-text="Keep as is"
        confirm-variant="primary"
        :loading="retiering"
        :dismissible="!retiering"
        @confirm="applyRetier"
        @cancel="retierPrompt = null"
      >
        <template v-if="retierPrompt">
          Commission changes from {{ formatMoney(retierPrompt.total_before) }} to {{ formatMoney(retierPrompt.total_after) }}.
          Owed after the change: {{ formatMoney(retierPrompt.owed_after) }}.
          <template v-if="toCents(retierPrompt.owed_after) < 0">
            The affiliate has been paid more than they are owed, so the balance goes negative until new commission covers it.
          </template>
        </template>
      </base-alert-dialog>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatCurrency, getGroupSettings } from '@/util/currencyFormat'
import { formatGroupDate } from '@/plugins/dates'

const props = defineProps({
  show: { type: Boolean, default: false },
  affiliate: { type: Object, default: null },
  link: { type: String, default: '' },
  canUpdate: { type: Boolean, default: false },
  tiers: { type: Array, default: () => [] }
})

const emit = defineEmits(['close', 'changed'])

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const group = JSON.parse(localStorage.getItem('group') || '{}')
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${group.id}`

const loadingPerformance = ref(false)
const sections = ref({ overview: true, performance: false, ledger: false, payout: false })
const savingTier = ref(false)
const selectedTierId = ref<any>(null)

// Archived tiers are hidden except the one currently assigned.
const tierOptions = computed(() =>
  (props.tiers as any[]).filter((t: any) => t.is_archived === 0 || t.id === selectedTierId.value))

const currentTierName = computed(() => {
  const match = (props.tiers as any[]).find((t: any) => t.id === selectedTierId.value)
  if (match) return match.name
  return (props.affiliate && props.affiliate.tier_name) || 'No tier'
})

async function onTierChange(tierId: any) {
  if (tierId === selectedTierId.value || savingTier.value) return
  const previous = selectedTierId.value
  savingTier.value = true
  selectedTierId.value = tierId
  try {
    const config = await authConfig()
    await axios.put(`${baseUrl}/affiliates/${props.affiliate.id}`, { affiliate_tier_id: tierId }, config)
    proxy.$notify({ type: 'success', message: 'Tier updated', icon: 'tim-icons icon-check-2' })
    emit('changed')
  } catch (err: any) {
    selectedTierId.value = previous
    proxy.$notify({ type: 'danger', message: errMessage(err, 'Failed to update tier'), icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    savingTier.value = false
  }
}
const performance = ref<any>(null)
const ledger = ref<any[]>([])
const recordingPayout = ref(false)
const payoutErr = ref<string | boolean>(false)
const payoutForm = ref<any>(emptyPayoutForm())
const attaching = ref(false)
const attachOpen = ref(false)
// base-input's `error` prop is typed String; never bind false to it.
const attachErr = ref('')
const attachForm = ref<any>({ order_number: '' })

const methodOptions = [
  { value: 'zelle', label: 'Zelle' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'other', label: 'Other' }
]

const typeLabels: any = { influencer: 'Influencer', local_business: 'Local business', player: 'Player' }

// House rule: money math in integer cents via exponential notation.
function toCents(x: any) {
  const n = parseFloat(x)
  if (Number.isNaN(n)) return 0
  if (Math.abs(n) < 0.005) return 0
  return Math.round(Number(n + 'e2'))
}

const owedCents = computed(() => (performance.value ? toCents(performance.value.owed) : 0))

const removing = ref(false)
// The earned row a staff member is about to reverse; null closes the dialog.
const removePrompt = ref<any>(null)

// Transactions that already carry a reversed row; earned rows in this set get
// no Remove link and render struck through.
const reversedTransactionIds = computed(() => {
  const ids = new Set<number>()
  for (const entry of ledger.value) {
    if (entry.entry_type === 'reversed' && entry.transaction_id != null) ids.add(Number(entry.transaction_id))
  }
  return ids
})

function isReversed(entry: any) {
  return entry.transaction_id != null && reversedTransactionIds.value.has(Number(entry.transaction_id))
}

function canRemove(entry: any) {
  return props.canUpdate && entry.entry_type === 'earned' && entry.transaction_id != null && !isReversed(entry)
}

// Select mode for moving earned entries to another tier.
const retierMode = ref(false)
const retierTierId = ref<any>(null)
const selectedIds = ref<Set<number>>(new Set())
const previewing = ref(false)
const retiering = ref(false)
// Preview response from the lambda; null closes the confirm dialog.
const retierPrompt = ref<any>(null)

// Archived tiers cannot receive entries; the lambda refuses them.
const activeTierOptions = computed(() => (props.tiers as any[]).filter((t: any) => t.is_archived === 0))

const eligibleEntries = computed(() => ledger.value.filter((e) => canRemove(e)))

function isSelected(entry: any) {
  return selectedIds.value.has(Number(entry.transaction_id))
}

// Vue 2.7 does not track Set mutation; every change reassigns a new Set.
function toggleSelected(entry: any, on: boolean) {
  const next = new Set(selectedIds.value)
  const id = Number(entry.transaction_id)
  if (on) next.add(id); else next.delete(id)
  selectedIds.value = next
}

function selectAll() {
  selectedIds.value = new Set(eligibleEntries.value.map((e) => Number(e.transaction_id)))
}


function openRetier() {
  retierMode.value = true
  const current = activeTierOptions.value.find((t: any) => t.id === selectedTierId.value)
  retierTierId.value = current ? current.id : null
  selectedIds.value = new Set()
}

function closeRetier() {
  retierMode.value = false
  retierTierId.value = null
  selectedIds.value = new Set()
  retierPrompt.value = null
}

function pluralEntries(n: number) {
  return n === 1 ? '1 entry' : `${n} entries`
}

const owedAfterRemovalCents = computed(() =>
  removePrompt.value ? owedCents.value - toCents(removePrompt.value.commission_amount) : 0)

function centsToDisplay(cents: number) {
  // formatMoney takes the dollar string convention the ledger rows use.
  return formatMoney((cents / 100).toFixed(2))
}

function emptyPayoutForm() {
  return { amount: null, payout_method: 'zelle', payout_reference: '', payout_note: '' }
}

function typeLabel(type: string) {
  return typeLabels[type] || type
}

function entryBadgeType(entryType: string) {
  if (entryType === 'earned') return 'success'
  if (entryType === 'reversed') return 'danger'
  return 'info'
}

function formatMoney(value: any) {
  return formatCurrency(parseFloat(value) || 0, {
    currency: group.currency,
    currencyFormat: group.currency_format,
    numberFormat: group.number_format
  })
}

function formatDate(d: any) {
  return formatGroupDate(d, { dayOfWeek: false })
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function errMessage(err: any, fallback: string) {
  const data = err?.response?.data
  if (data) {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data
      if (parsed && parsed.error) return parsed.error
    } catch (e) { /* not JSON */ }
    if (typeof data === 'string' && data.length > 0 && data.length < 200) return data
  }
  return fallback
}

async function loadPerformance() {
  if (!props.affiliate) return
  loadingPerformance.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/affiliates/${props.affiliate.id}/performance`, config)
    performance.value = response.data
    ledger.value = response.data.ledger || []
    const stillEligible = new Set(ledger.value.filter((e) => canRemove(e)).map((e) => Number(e.transaction_id)))
    selectedIds.value = new Set([...selectedIds.value].filter((id) => stillEligible.has(id)))
    payoutForm.value = emptyPayoutForm()
    payoutForm.value.amount = toCents(response.data.owed) > 0 ? parseFloat(response.data.owed) : null
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: errMessage(err, 'Failed to load performance'), icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loadingPerformance.value = false
  }
}

async function copyLink() {
  try {
    await navigator.clipboard.writeText(props.link)
    proxy.$notify({ type: 'success', message: 'Booking link copied', icon: 'tim-icons icon-check-2' })
  } catch (e) {
    proxy.$notify({ type: 'danger', message: 'Could not copy the link', icon: 'tim-icons icon-alert-circle-exc' })
  }
}

async function recordPayout() {
  payoutErr.value = false
  const amount = payoutForm.value.amount
  const amountCents = toCents(amount)
  if (amountCents <= 0) {
    payoutErr.value = 'Enter an amount greater than zero'
    return
  }
  if (amountCents > owedCents.value) {
    payoutErr.value = 'Payout exceeds amount owed'
    return
  }

  recordingPayout.value = true
  try {
    const config = await authConfig()
    const body = {
      amount,
      payout_method: payoutForm.value.payout_method,
      payout_reference: payoutForm.value.payout_reference || null,
      payout_note: payoutForm.value.payout_note || null
    }
    await axios.post(`${baseUrl}/affiliates/${props.affiliate.id}/payouts`, body, config)
    proxy.$notify({ type: 'success', message: 'Payout recorded', icon: 'tim-icons icon-check-2' })
    await loadPerformance()
    emit('changed')
  } catch (err: any) {
    payoutErr.value = errMessage(err, 'Failed to record payout')
    proxy.$notify({ type: 'danger', message: String(payoutErr.value), icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    recordingPayout.value = false
  }
}

function closeAttach() {
  attachOpen.value = false
  attachForm.value = { order_number: '' }
  attachErr.value = ''
}

async function attachTransaction() {
  attachErr.value = ''
  const orderNumber = String(attachForm.value.order_number || '').trim().replace(/^#/, '')
  if (!orderNumber) {
    attachErr.value = 'Enter a transaction number'
    return
  }
  attaching.value = true
  try {
    const config = await authConfig()
    await axios.post(`${baseUrl}/affiliates/${props.affiliate.id}/transactions`, { order_number: orderNumber }, config)
    proxy.$notify({ type: 'success', message: 'Transaction added to the ledger', icon: 'tim-icons icon-check-2' })
    closeAttach()
    await loadPerformance()
    emit('changed')
  } catch (err: any) {
    attachErr.value = errMessage(err, 'Failed to add the transaction')
    proxy.$notify({ type: 'danger', message: String(attachErr.value), icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    attaching.value = false
  }
}

async function removeTransaction() {
  const entry = removePrompt.value
  if (!entry || removing.value) return
  removing.value = true
  try {
    const config = await authConfig()
    await axios.delete(`${baseUrl}/affiliates/${props.affiliate.id}/transactions/${entry.transaction_id}`, config)
    proxy.$notify({ type: 'success', message: 'Transaction removed from the ledger', icon: 'tim-icons icon-check-2' })
    removePrompt.value = null
    await loadPerformance()
    emit('changed')
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: errMessage(err, 'Failed to remove the transaction'), icon: 'tim-icons icon-alert-circle-exc' })
    removePrompt.value = null
    // A 409 means someone else reversed it first; refresh so the link disappears.
    if (err?.response?.status === 409) await loadPerformance()
  } finally {
    removing.value = false
  }
}

async function previewRetier() {
  if (previewing.value || selectedIds.value.size === 0 || !retierTierId.value) return
  previewing.value = true
  try {
    const config = await authConfig()
    const body = { affiliate_tier_id: retierTierId.value, transaction_ids: [...selectedIds.value], preview: true }
    const response = await axios.put(`${baseUrl}/affiliates/${props.affiliate.id}/transactions`, body, config)
    retierPrompt.value = response.data
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: errMessage(err, 'Failed to calculate the new commission'), icon: 'tim-icons icon-alert-circle-exc' })
    if (err?.response?.status === 409) await loadPerformance()
  } finally {
    previewing.value = false
  }
}

async function applyRetier() {
  const prompt = retierPrompt.value
  if (!prompt || retiering.value) return
  retiering.value = true
  try {
    const config = await authConfig()
    const body = { affiliate_tier_id: prompt.tier.id, transaction_ids: prompt.rows.map((r: any) => r.transaction_id) }
    await axios.put(`${baseUrl}/affiliates/${props.affiliate.id}/transactions`, body, config)
    proxy.$notify({ type: 'success', message: `Tier updated on ${pluralEntries(prompt.rows.length)}`, icon: 'tim-icons icon-check-2' })
    closeRetier()
    await loadPerformance()
    emit('changed')
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: errMessage(err, 'Failed to change the tier'), icon: 'tim-icons icon-alert-circle-exc' })
    retierPrompt.value = null
    if (err?.response?.status === 409) await loadPerformance()
  } finally {
    retiering.value = false
  }
}

function onEscKey(e: KeyboardEvent) {
  if (e.key !== 'Escape') return
  if (retierPrompt.value) {
    if (!retiering.value) retierPrompt.value = null
    return
  }
  // While the remove confirm is open, ESC dismisses it rather than the drawer.
  if (removePrompt.value) {
    if (!removing.value) removePrompt.value = null
    return
  }
  emit('close')
}

watch(() => props.show, (val) => {
  if (val) {
    document.addEventListener('keydown', onEscKey)
    performance.value = null
    ledger.value = []
    selectedTierId.value = props.affiliate ? props.affiliate.affiliate_tier_id : null
    sections.value = { overview: true, performance: false, ledger: false, payout: false }
    closeAttach()
    removePrompt.value = null
    closeRetier()
    loadPerformance()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})
</script>

<script lang="ts">
// Badge is exported from components/index.js but NOT registered by
// globalComponents.js; register locally like other consumers. The explicit
// .vue extension matters in lang="ts" blocks: TS only matches the
// shims-vue.d.ts '*.vue' declaration when the extension is present.
import Badge from '@/components/Badge.vue'

export default {
  name: 'affiliate-detail-drawer',
  components: { badge: Badge }
}
</script>

<style scoped lang="scss">
// Copied from BookingDrawer's slot info section (.detail-row, BookingDrawer.vue:5205).
.detail-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
}
// Same text treatment as .detail-row (BookingDrawer language); the theme's
// <small> color is too faint on the dark drawer background.
.ledger-row {
  font-size: 13px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(125, 125, 125, 0.2);
  color: rgba(255, 255, 255, 0.7);
  &:last-child { border-bottom: none; }

  small {
    color: inherit;
  }
}
// Inline editable badge + hidden el-select, copied from BookingDrawer's
// workflow state pattern (BookingDrawer.vue:5215-5275). el-select is kept for
// its body-level popper (avoids being clipped by the drawer's overflow:auto).
.status-badge-inline {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
}
.status-badge-inline--editable {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  position: relative;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(0.92);
  }
}
.status-badge-inline__icon {
  font-size: 10px;
  opacity: 0.85;
}
.status-badge-inline__select {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;

  ::v-deep .el-input,
  ::v-deep .el-input__inner,
  ::v-deep .el-input__suffix {
    height: 100%;
    border: none;
    padding: 0;
    background: transparent;
  }
}

// Select-mode toolbar: the tier select is a fixed-width control on one line
// with the action, not a full-width labeled form field.
.retier-toolbar__select {
  width: 150px;
  flex: 0 0 auto;
}

// Row selection uses the theme checkbox (17px box, primary fill). The theme's
// .form-check is built for a labeled form field: it adds a top margin and
// reserves 25px of label padding. Neither applies to a bare box inline with
// the badge, so both are zeroed and the box gets a fixed gap to the badge.
.ledger-select.form-check {
  margin: 0 10px 0 0;
  line-height: 0;
  ::v-deep .form-check-label {
    padding-left: 17px;
    height: 17px;
    line-height: 17px;
  }
}

// Copied from BookingDrawer's .section-header__link (BookingDrawer.vue:5393).
.section-header__link {
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;

  &:hover { text-decoration: none; opacity: 0.8; }
}
</style>

<style lang="scss">
// Light mode parity, namespaced under the drawer root so the generic class
// names cannot leak app-wide. Mirrors .white-content .booking-drawer .detail-row.
.white-content .affiliate-detail-drawer .detail-row,
.white-content .affiliate-detail-drawer .ledger-row {
  color: #525f7f;
}
// Neutral tint badge colors are tuned for dark mode via inline :style;
// override for light like BookingDrawer's unassigned badge does.
.white-content .affiliate-detail-drawer .status-badge-inline--unassigned {
  background: rgba(0, 0, 0, 0.06) !important;
  color: rgba(0, 0, 0, 0.65) !important;
}
</style>
